import { ref } from 'vue';
import store from '@/store';
import { uniqBy, filter } from 'lodash';
import { scpmUtil } from '@/utils/scpmUtil';
import { constant } from '@/utils/constant';
import { useCheckAuth } from '@/composables/useCheckAuthorization';
import { PRODUCT_SUB_TYPE_OBJECT_NAME } from '@/containers/utils/constant';
import { restManager } from '@/plugin/restManager';
import { getProducts } from '@/containers/components/chart/utils';

export default function useChartActions({
  selectedTargets,
  selectedSubProduct,
  selectedProductType,
  start,
  end,
  metricData,
  individualItems
}) {
  const { checkAuth } = useCheckAuth();

  const widgetConfig = ref(null);
  const isShowAddWidgetPopup = ref(false);
  const isAddEventPolicyPopup = ref(false);
  const selectedTargetAddSetting = ref({
    metric: null,
    targets: []
  });
  const searchDate = {
    queryStartDt: start.value,
    queryEndDt: end.value
  };

  const checkProductSubType = async () => {
    const subType = await getProductObjectTypes(selectedProductType.value);

    return subType && subType.length > 0;
  };
  const conditionSubType = () => {
    const conditionSubType = new Map(PRODUCT_SUB_TYPE_OBJECT_NAME);

    return !(conditionSubType.get(selectedProductType.value) === selectedSubProduct.value);
  };

  const getSelectedIndividualInsightChart = () => {
    return metricData.value?.selectedIndividualItems || [];
  };

  const addWidgetPopup = async (metric, chartType, popupKey) => {
    const listId = getSelectedIndividualInsightChart(metric.metricKey);
    const individuals =
      individualItems.value && individualItems.value.length
        ? individualItems.value.filter((item) => listId.includes(item.value))
        : null;
    let objectItems = [];
    let products = getProducts(selectedTargets.value);
    const hasSubType = await checkProductSubType();

    if (conditionSubType() && hasSubType) {
      objectItems = scpmUtil.replaceArr(selectedTargets.value, {
        objectName: 'key',
        objectDisplayName: 'text',
        resourceId: 'productResourceId',
        idx: 'idx',
        check: 'check'
      });

      objectItems = filter(objectItems, (item) => item.productResourceId);
      products = uniqBy(products, 'key');
    }

    widgetConfig.value = {
      chartAttr: {
        widget: {
          type: 'TimeSeriesGraphWidget',
          options: {
            chartType: chartType,
            isObjectExist: metric.isObjectExist,
            disableObject: metric.disableObject,
            metricName: metric.metricName,
            metricKey: metric.metricKey,
            objectItems: objectItems,
            objectType: selectedSubProduct.value || null,
            objects: individuals,
            products: products,
            resourceType: selectedProductType.value
          }
        }
      }
    };

    isShowAddWidgetPopup.value = true;
    setChartPopupState(popupKey);
  };

  const eventAddModal = (item, popupKey) => {
    const productResourceIds = [];
    const listDataCheckAuth = [];
    let products = getProducts(selectedTargets.value, false);
    resetDataAddSetting();
    selectedTargetAddSetting.value.metric = item;
    if (products.length) {
      products.forEach((product) => {
        const productResourceId = product.key || product.productResourceId || product.resourceId;
        const productName = product.text || product.productName;
        productResourceIds.push(productResourceId);
        listDataCheckAuth.push({
          productResourceId,
          productName
        });
      });
      const productTypeCode = selectedProductType.value;
      const callback = (data) => {
        if (data.type === constant.AuthorizationResult.ALLOW) {
          products.forEach((product) => {
            const productName = product.text || product.productName;
            const productSq = product.productId || product.productSq;
            selectedTargetAddSetting.value.targets.push({
              ...product,
              productSq,
              label: productName,
              value: productName
            });
          });

          isAddEventPolicyPopup.value = true;
          setChartPopupState(popupKey);
        }
      };
      const errorCallback = (err) => {
        scpmUtil.alertModal(err);
      };

      checkAuth(
        {
          productTypeCode,
          productResourceIds,
          listDataCheckAuth,
          actionName: constant.AuthorizationAction.MONITOR_VIEW
        },
        callback,
        errorCallback
      );
    } else {
      isAddEventPolicyPopup.value = true;
    }
  };

  const resetDataAddSetting = () => {
    selectedTargetAddSetting.value = {
      metric: null,
      targets: []
    };
  };

  const closeAddWidgetPopup = (id) => {
    removeChartPopupState(id);
    isShowAddWidgetPopup.value = false;
  };

  const closeEventPolicyPopup = (id) => {
    removeChartPopupState(id);
    isAddEventPolicyPopup.value = false;
  };

  const removeChartPopupState = (id) => {
    store.dispatch('chartPopup/removePopup', id);
  };

  const setChartPopupState = (id) => {
    store.dispatch('chartPopup/setPopupVisibleStates', id);
  };

  const getPopupZIndex = (id) => {
    return store.getters['chartPopup/getPopupZIndexById'](id);
  };

  const getProductObjectTypes = async (productTypeCode) => {
    return await restManager.product.getListProductObjectTypes(productTypeCode);
  };

  return {
    widgetConfig,
    searchDate,
    isShowAddWidgetPopup,
    isAddEventPolicyPopup,
    selectedTargetAddSetting,
    addWidgetPopup,
    eventAddModal,
    getPopupZIndex,
    getProductObjectTypes,
    resetDataAddSetting,
    closeAddWidgetPopup,
    closeEventPolicyPopup
  };
}
